/*
 * <custom-header>
 * バナー開閉 (sessionStorage) / 検索パネル / ナビドロワー / 折りたたみ (CATEGORIES・PROJECTS)。
 */
const CHDR_BANNER_KEY = 'pvz_banner_closed';

if (!customElements.get('custom-header')) {
  customElements.define(
    'custom-header',
    class CustomHeader extends HTMLElement {
      connectedCallback() {
        this.banner = this.querySelector('[data-chdr-banner]');
        this.search = this.querySelector('[data-chdr-search]');
        this.searchInput = this.querySelector('[data-chdr-search-input]');
        this.nav = this.querySelector('[data-chdr-nav]');
        this.overlay = this.querySelector('.chdr__overlay');
        this.menuToggle = this.querySelector('[data-chdr-nav-toggle].chdr__menu-toggle');

        // メニュー高さを :root に公開 (#MainContent の押し下げ量に使用)
        const navHeight = this.style.getPropertyValue('--chdr-nav-height') || '400px';
        document.documentElement.style.setProperty('--chdr-nav-height', navHeight);

        this.initBanner();

        this.querySelectorAll('[data-chdr-search-toggle]').forEach((el) =>
          el.addEventListener('click', () => this.toggleSearch())
        );
        this.querySelectorAll('[data-chdr-nav-toggle]').forEach((el) =>
          el.addEventListener('click', () => this.toggleNav())
        );
        this.querySelectorAll('[data-chdr-collapse]').forEach((el) =>
          el.addEventListener('click', () => this.toggleCollapse(el))
        );

        this.onKeydown = (event) => {
          if (event.key === 'Escape') {
            this.closeNav();
            this.closeSearch();
          }
        };
        this.onResize = () => this.measure();
        window.addEventListener('resize', this.onResize);
        this.measure();
        this.initAutoHide();
      }

      // ---------- バナー ----------
      initBanner() {
        if (!this.banner) return;
        let closed = false;
        try {
          closed = sessionStorage.getItem(CHDR_BANNER_KEY) === '1';
        } catch (e) {
          /* sessionStorage 不可環境では常に表示 */
        }
        this.banner.hidden = closed;
        this.measure();

        const closeBtn = this.querySelector('[data-chdr-banner-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.banner.hidden = true;
            try {
              sessionStorage.setItem(CHDR_BANNER_KEY, '1');
            } catch (e) {
              /* noop */
            }
            this.measure();
          });
        }
      }

      // ヘッダー実高さを CSS 変数へ (PC パネルの開始位置に使用)
      measure() {
        const bar = this.querySelector('.chdr__bar');
        const bannerH = this.banner && !this.banner.hidden ? this.banner.offsetHeight : 0;
        const barH = bar ? bar.offsetHeight : 0;
        const height = bannerH + barH + 'px';
        this.style.setProperty('--chdr-height', height);
        // カテゴリーナビなど別セクションが参照できるよう :root にも公開
        document.documentElement.style.setProperty('--chdr-height', height);
        this.headerHeight = bannerH + barH;
        this.updateOffset();
      }

      // ---------- スクロールで隠す / 上スクロールで出す ----------
      // 下スクロール: ヘッダーを隠す / 上スクロール: 出す。
      // PC (990px 以上。PC 用のメニューバーを出す幅) は隠さず、常に表示する。
      // コレクション一覧の固定行はこの下に貼り付くので、ヘッダーの表示状態に
      // 合わせて --chdr-offset (固定行の top) を 0 か ヘッダー高さに切り替える。
      initAutoHide() {
        this.lastScroll = window.scrollY;
        this.hidden_ = false;
        this.pcQuery = window.matchMedia('(min-width: 990px)');
        // スマホで隠れた状態のまま PC 幅に広げたときは出しておく
        this.pcQuery.addEventListener('change', (event) => {
          if (event.matches) this.setHidden(false);
        });

        this.onScroll = () => {
          if (this.scrollTicking) return;
          this.scrollTicking = true;
          requestAnimationFrame(() => {
            const y = Math.max(0, window.scrollY);
            const delta = y - this.lastScroll;

            // ナビや検索を開いているあいだは隠さない
            const locked = this.classList.contains('is-nav-open') || (this.search && !this.search.hidden);

            if (locked || y <= this.headerHeight || this.pcQuery.matches) {
              this.setHidden(false);
            } else if (delta > 4) {
              this.setHidden(true);
            } else if (delta < -4) {
              this.setHidden(false);
            }

            this.lastScroll = y;
            this.scrollTicking = false;
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      setHidden(hidden) {
        if (this.hidden_ === hidden) return;
        this.hidden_ = hidden;
        this.classList.toggle('is-hidden', hidden);
        // セクションのラッパーは sticky のまま画面上部に残るため、隠れているあいだは
        // クリックを透過させる (下にあるコレクション固定行のボタンを押せるように)
        const wrapper = this.closest('.chdr-section');
        if (wrapper) wrapper.classList.toggle('is-hidden', hidden);
        this.updateOffset();
      }

      updateOffset() {
        const offset = this.hidden_ ? 0 : this.headerHeight || 0;
        document.documentElement.style.setProperty('--chdr-offset', offset + 'px');
      }

      // ---------- 検索 ----------
      toggleSearch() {
        if (!this.search) return;
        const willOpen = this.search.hidden;
        this.search.hidden = !willOpen;
        this.querySelectorAll('[data-chdr-search-toggle]').forEach((el) =>
          el.setAttribute('aria-expanded', willOpen ? 'true' : 'false')
        );
        if (willOpen && this.searchInput) this.searchInput.focus();
      }

      closeSearch() {
        if (this.search && !this.search.hidden) this.toggleSearch();
      }

      // ---------- ナビ ----------
      toggleNav() {
        if (this.classList.contains('is-nav-open')) {
          this.closeNav();
        } else {
          this.openNav();
        }
      }

      openNav() {
        this.measure();
        this.classList.add('is-nav-open');
        document.body.classList.add('chdr-nav-open');
        if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this.onKeydown);
      }

      closeNav() {
        this.classList.remove('is-nav-open');
        document.body.classList.remove('chdr-nav-open');
        if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', this.onKeydown);
      }

      // ---------- 折りたたみ ----------
      toggleCollapse(head) {
        const body = head.nextElementSibling;
        const expanded = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (body) body.hidden = expanded;
      }
    }
  );
}

/**
 * カートの個数表示 (CART 2) を、カートページでの数量変更・削除・ギフトラッピングにも追従させる。
 * cart.js / pvz-gift-wrap.js は更新後に PUB_SUB_EVENTS.cartUpdate で新しいカート (cartData) を通知する。
 * 商品ページからの追加は pvz-cart-added.js が同じ要素を書き換えている。
 */
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const count = event && event.cartData ? event.cartData.item_count : undefined;
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-chdr-cart-count]').forEach((el) => {
      el.textContent = count > 9 ? '9+' : String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-chdr-cart-link]').forEach((el) => {
      el.hidden = count === 0;
    });
  });
}
